/** 位置履歴リングバッファの最大件数 */
const POSITION_HISTORY_MAX_LENGTH = 30;

/** 位置情報購読者を識別する機能名（新機能を追加する場合はここに1行追加する） */
export const GEOLOCATION_SUBSCRIBER_FEATURES = {
  /** 駅エリア判定 */
  STATION_AREA: 'station-area',
  /** 速度監視 */
  SPEED_MONITORING: 'speed-monitoring',
} as const;

/** 位置情報購読者を識別する機能名の型 */
export type GeolocationSubscriberFeature
  = typeof GEOLOCATION_SUBSCRIBER_FEATURES[keyof typeof GEOLOCATION_SUBSCRIBER_FEATURES];

/**
 * 駅エリア検証範囲
 */
export type PositionValidationArea = {
  /** 左下緯度 */
  lat1: number;
  /** 左下経度 */
  lon1: number;
  /** 右上緯度 */
  lat2: number;
  /** 右上経度 */
  lon2: number;
  /** 駅エリア検知フラグ */
  isAreaDetectionEnabled?: boolean;
};

/**
 * 位置情報取得時のエラー情報
 */
export type GeolocationAcquisitionError = {
  code?: number;
  message: string;
  type: 'PERMISSION_DENIED' | 'POSITION_UNAVAILABLE' | 'TIMEOUT' | 'UNSUPPORTED' | 'UNKNOWN';
};

/**
 * 警告抑制（OKボタンによる一時非表示）を許可するGPSエラー種別。
 *
 * 電波状況など環境起因で位置情報を取得できない場合は業務を塞がないよう抑制を許可する。
 * 権限なし（PERMISSION_DENIED）・端末非対応（UNSUPPORTED）・原因不明（UNKNOWN）は
 * 利用者側の対処が必要な状態のため抑制対象外とする。
 */
const SUPPRESSIBLE_GPS_ERROR_TYPES: GeolocationAcquisitionError['type'][]
  = ['POSITION_UNAVAILABLE', 'TIMEOUT'];

type State = {
  // 生位置情報
  /** 直近取得した現在位置 */
  currentPosition: GeolocationPosition | null;
  /** 直近最大 POSITION_HISTORY_MAX_LENGTH 件の位置履歴（古い→新しい順） */
  positionHistory: GeolocationPosition[];
  /** 位置情報取得時に発生したエラー情報 */
  acquisitionError: GeolocationAcquisitionError | null;
  /** 最後に位置情報を更新した時刻 (Date.now()) */
  lastUpdateAt: number | null;

  // 速度監視関連の派生ステータス（useSpeedMonitorが書込）
  /** 直近算出した速度（m/s） */
  currentSpeed: number;
  /** 速度が閾値を超過していると確定しているか */
  isSpeedExceeded: boolean;
  /** GPSエラー状態かどうか */
  isGpsError: boolean;
  /** GPSエラー状態の要因となったエラー種別。GPSエラーでない場合はnull */
  gpsErrorType: GeolocationAcquisitionError['type'] | null;
  /** 速度超過警告を一時的に抑制しているか */
  isWarningSuppressed: boolean;
  /** 警告抑制継続時間（ミリ秒） */
  warningSuppressDurationMs: number | null;

  // 駅エリア判定関連の派生ステータス（usePositionValidationが書込）
  /** 現在位置が駅エリア内と判定されているか */
  isInsideArea: boolean;
  /** エリア内判定が連続成功した回数 */
  consecutiveSuccess: number;
  /** 駅エリア内判定確定に必要な連続成功回数（system config由来） */
  stationAreaStayThreshold: number | null;
  /** 駅エリア検証に使用する判定範囲 */
  validationArea: PositionValidationArea | null;
  /** 駅エリア判定時に表示するエラーメッセージ */
  validationError: string | null;

  /** 位置情報取得の購読者（機能識別子） */
  subscribers: Set<GeolocationSubscriberFeature>;
};

/**
 * GPS位置情報と派生ステータスを集約管理するストア。
 *
 * 値の保持に専念する。
 * watchPositionのライフサイクル・判定ロジック・タイマー類は composable 側で実装する。
 */
export const useGeolocationStore = defineStore('geolocation', {
  state: (): State => ({
    currentPosition: null,
    positionHistory: [],
    acquisitionError: null,
    lastUpdateAt: null,

    currentSpeed: 0,
    isSpeedExceeded: false,
    isGpsError: false,
    gpsErrorType: null,
    isWarningSuppressed: false,
    warningSuppressDurationMs: null,

    isInsideArea: false,
    consecutiveSuccess: 0,
    stationAreaStayThreshold: null,
    validationArea: null,
    validationError: null,

    subscribers: new Set<GeolocationSubscriberFeature>(),
  }),
  getters: {
    /** Geolocation APIが使用可能か */
    isSupported(): boolean {
      return typeof navigator !== 'undefined' && 'geolocation' in navigator;
    },
    /** 駅エリア判定処理が動作中か */
    isValidating(): boolean {
      return this.subscribers.has(GEOLOCATION_SUBSCRIBER_FEATURES.STATION_AREA);
    },
    /** 速度監視処理が動作中か */
    isMonitoring(): boolean {
      return this.subscribers.has(GEOLOCATION_SUBSCRIBER_FEATURES.SPEED_MONITORING);
    },
    /** 購読者が一人もいないか（watchPositionの停止判定用） */
    hasNoSubscribers(): boolean {
      return this.subscribers.size === 0;
    },
    /** 現在のGPSエラーが警告抑制（OKボタンによる一時非表示）を許可する種別か */
    isSuppressibleGpsError(): boolean {
      return this.gpsErrorType !== null && SUPPRESSIBLE_GPS_ERROR_TYPES.includes(this.gpsErrorType);
    },
  },
  actions: {
    // ---------- 位置情報の生データ ----------

    /** 位置情報の更新を反映する。位置履歴に追加し、エラー状態をクリアする */
    applyPositionUpdate(pos: GeolocationPosition) {
      this.currentPosition = pos;
      this.positionHistory.push(pos);
      if (this.positionHistory.length > POSITION_HISTORY_MAX_LENGTH) {
        this.positionHistory.shift();
      }
      this.acquisitionError = null;
      this.lastUpdateAt = Date.now();
    },

    /** 位置情報取得エラーを設定する */
    setAcquisitionError(err: GeolocationAcquisitionError | null) {
      this.acquisitionError = err;
    },

    /** 位置情報の生データをクリアする */
    clearPositionData() {
      this.currentPosition = null;
      this.positionHistory = [];
      this.acquisitionError = null;
      this.lastUpdateAt = null;
    },

    // ---------- 購読者管理 ----------

    /** 購読者を追加する */
    addSubscriber(feature: GeolocationSubscriberFeature) {
      this.subscribers.add(feature);
    },

    /** 購読を解除する */
    removeSubscriber(feature: GeolocationSubscriberFeature) {
      this.subscribers.delete(feature);
    },

    // ---------- 速度監視関連の状態 ----------

    /** 速度関連の派生ステータスを部分更新する */
    setSpeedFields(payload: Partial<{
      currentSpeed: number;
      isSpeedExceeded: boolean;
      isGpsError: boolean;
      gpsErrorType: GeolocationAcquisitionError['type'] | null;
    }>) {
      Object.assign(this, payload);
    },

    /** 速度監視関連の派生ステータスを初期値にリセットする */
    resetSpeedStatus() {
      this.currentSpeed = 0;
      this.isSpeedExceeded = false;
      this.isGpsError = false;
      this.gpsErrorType = null;
      this.isWarningSuppressed = false;
      this.warningSuppressDurationMs = null;
    },

    /** 警告抑制状態を設定する */
    setWarningSuppressed(value: boolean) {
      this.isWarningSuppressed = value;
    },

    /** 警告抑制継続時間を設定する */
    setWarningSuppressDuration(ms: number) {
      this.warningSuppressDurationMs = ms;
    },

    // ---------- 駅エリア判定関連の状態 ----------

    /** エリア関連の派生ステータスを部分更新する */
    setAreaFields(payload: Partial<{
      isInsideArea: boolean;
      consecutiveSuccess: number;
      validationError: string | null;
    }>) {
      Object.assign(this, payload);
    },

    /** 駅エリア検証範囲を設定/解除する */
    setValidationArea(area: PositionValidationArea | null) {
      this.validationArea = area;
    },

    /** 駅エリア内判定確定に必要な連続成功回数を設定する */
    setStationAreaStayThreshold(value: number | null) {
      this.stationAreaStayThreshold = value;
    },

    /** エリア判定関連の派生ステータスを初期値にリセットする */
    resetAreaStatus() {
      this.isInsideArea = false;
      this.consecutiveSuccess = 0;
      this.stationAreaStayThreshold = null;
      this.validationArea = null;
      this.validationError = null;
    },
  },
});
